import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  notification,
  Row,
  Col,
} from "antd";
import { toUTCMidnight } from "../utils/dateUtils";
import { DATE_PICKER_FORMAT } from "../constants/dateConstants";
import { useMutation } from "@tanstack/react-query";
import { createTask, fetchNextTaskNumber } from "../services/tasksAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import { handleIntegerKeyDown, createIntegerChangeHandler } from "../utils/textUtils";
import AttachmentsSection from "./AttachmentsSection";
import SimpleRichTextEditor from "./SimpleRichTextEditor";
import ProjectSettingToggle from "./ProjectSettingToggle";
import ProjectUserSelect from "./ProjectUserSelect";

const PRIORITY_OPTIONS = [
  { value: "High", label: "High" },
  { value: "Medium", label: "Medium" },
  { value: "Low", label: "Low" },
];

const REMINDER_OPTIONS = [
  { value: "None", label: "None" },
  { value: "Daily on weekdays", label: "Daily on weekdays" },
  { value: "Twice a week", label: "Twice a week" },
  { value: "Weekly", label: "Weekly" },
  { value: "Biweekly", label: "Biweekly" },
  { value: "Once a month", label: "Once a month" },
];

const CreateTaskModal = ({ visible, onClose }) => {
  const { selectedProject, currentUser } = useAuth();
  const projectId = selectedProject.project_id;
  const [form] = Form.useForm();
  const [sendEmail, setSendEmail] = useState(true);
  const statusValue = Form.useWatch("status", form);
  const emailNotificationsDisabled = statusValue ? statusValue !== "Open" : false;

  useEffect(() => {
    if (visible && projectId) {
      fetchNextTaskNumber(projectId).then((data) => {
        form.resetFields();
        form.setFieldsValue({
          number: data.next_number,
          task_manager_id: currentUser.id,
          status: "Open",
          priority: "Medium",
          reminder_frequency: "None",
          require_attachment_for_responses: false,
        });
      });
    }
  }, [visible, projectId, form, currentUser.id]);

  useEffect(() => {
    if (statusValue && emailNotificationsDisabled) {
      setSendEmail(false);
      form.setFieldsValue({ email_notifications: false });
    }
  }, [emailNotificationsDisabled, form, statusValue]);

  const mutation = useMutation({
    mutationFn: ({ taskData, sendEmail }) => createTask(taskData, sendEmail),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tasks", projectId] });
      onClose();
      form.resetFields();
      setSendEmail(true);
      notification.success({ message: "Task created successfully" });
    },
    onError: (error) => {
      const detail = error.response?.data?.detail;
      notification.error({
        message: "Failed to create task",
        description: detail?.message || detail || error.message,
      });
    },
  });

  const handleCreateTask = (values) => {
    const assigneeIds = values.assignee_ids || [];
    const taskData = {
      ...values,
      project_id: projectId,
      attachments: values.attachments?.map((file) => file) || [],
      due_date: values.due_date ? toUTCMidnight(values.due_date) : null,
      ball_in_court_ids: assigneeIds,
    };
    mutation.mutate({ taskData, sendEmail });
  };

  const handleClose = () => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "Unsaved Changes",
        content: "You have unsaved changes. Are you sure you want to close without saving?",
        okText: "Yes, Close",
        okType: "danger",
        cancelText: "Cancel",
        onOk: () => {
          form.resetFields();
          setSendEmail(true);
          onClose();
        },
      });
    } else {
      form.resetFields();
      setSendEmail(true);
      onClose();
    }
  };

  return (
    <Modal title="Create Task" open={visible} onCancel={handleClose} footer={null} className="w-full max-w-4xl">
      <Form form={form} onFinish={handleCreateTask} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Number"
              name="number"
              rules={[
                { required: true, message: "Please input the number!" },
                { pattern: /^\d+$/, message: "Please enter only whole numbers!" },
              ]}
            >
              <Input
                type="number"
                step="1"
                onKeyDown={handleIntegerKeyDown}
                onChange={createIntegerChangeHandler(form, "number")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Task Manager"
              name="task_manager_id"
              rules={[{ required: true, message: "Please select a task manager!" }]}
            >
              <ProjectUserSelect
                mode={null}
                placeholder="Select task manager"
                className="w-full"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Title" name="title" rules={[{ required: true, message: "Please input the title!" }]}>
          <Input />
        </Form.Item>

        <Form.Item
          label="Task Detail"
          name="task_detail"
          rules={[{ required: true, message: "Please input the task detail!" }]}
        >
          <SimpleRichTextEditor placeholder="Enter task details..." aiEnhanceTarget="task_detail" />
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Status" name="status" initialValue="Open">
              <Select
                options={[
                  { value: "Open", label: "Open" },
                  { value: "Draft", label: "Draft" },
                  { value: "Closed", label: "Closed" },
                ]}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Priority" name="priority" initialValue="Medium">
              <Select options={PRIORITY_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label="Due Date" name="due_date">
              <DatePicker format={DATE_PICKER_FORMAT} className="w-full" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label="Reminder Frequency" name="reminder_frequency" initialValue="None">
              <Select options={REMINDER_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Assignees"
          name="assignee_ids"
          rules={[
            {
              validator: (_, value) =>
                value?.length ? Promise.resolve() : Promise.reject(new Error("Please select at least one assignee!")),
            },
          ]}
        >
          <ProjectUserSelect
            placeholder="Select at least one assignee"
            className="w-full"
          />
        </Form.Item>

        <Form.Item label="Distribution List (Cc)" name="distribution_list">
          <ProjectUserSelect
            placeholder="Select Cc"
            className="w-full"
          />
        </Form.Item>

        <Form.Item name="attachments" label="Attachments">
          <AttachmentsSection projectId={projectId} />
        </Form.Item>

        <div className="project-settings-grid">
          <ProjectSettingToggle
            name="require_attachment_for_responses"
            title="Require Attachment for Responses"
          />
        </div>

        <div className="project-settings-grid">
          <ProjectSettingToggle
            name="email_notifications"
            title="Email Notifications"
            description={
              emailNotificationsDisabled
                ? "Set status to Open to enable emails"
                : undefined
            }
            initialValue={true}
            checked={sendEmail}
            disabled={emailNotificationsDisabled}
            onChange={(checked) => {
              setSendEmail(checked);
              form.setFieldsValue({ email_notifications: checked });
            }}
          />
        </div>

        <Form.Item className="w-full">
          <Button
            type="primary"
            onClick={() => form.submit()}
            loading={mutation.isPending}
            className="w-full mt-4"
          >
            {sendEmail ? "Create and Send Emails" : "Create Task"}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTaskModal;
